import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { getItem, setItem, removeItem } from "@/lib/storage";
import { useAuth } from "./AuthContext";
import type { Club, Member } from "@/lib/types";

/* ── Types ── */

interface ClubContextType {
  club: (Club & { id: string }) | null;
  membership: (Member & { id: string }) | null;
  clubIds: string[];
  loading: boolean;
  switchClub: (clubId: string | null) => void;
}

const ClubContext = createContext<ClubContextType>({
  club: null,
  membership: null,
  clubIds: [],
  loading: true,
  switchClub: () => {},
});

/* ── Hook: subscribe to membership + club doc for a given club ── */

function useClubSubscription() {
  const memberUnsub = useRef<(() => void) | null>(null);
  const clubUnsub = useRef<(() => void) | null>(null);

  const cleanup = useCallback(() => {
    memberUnsub.current?.();
    clubUnsub.current?.();
    memberUnsub.current = null;
    clubUnsub.current = null;
  }, []);

  const subscribe = useCallback(
    (
      uid: string,
      clubId: string,
      onData: (m: (Member & { id: string }) | null, c: (Club & { id: string }) | null) => void,
      onReady: () => void
    ) => {
      cleanup();

      let memberData: (Member & { id: string }) | null = null;
      let clubData: (Club & { id: string }) | null = null;

      memberUnsub.current = onSnapshot(
        query(collection(db, "clubs", clubId, "members"), where("userId", "==", uid)),
        (snap) => {
          memberData = snap.empty
            ? null
            : ({ id: snap.docs[0].id, ...snap.docs[0].data() } as Member & { id: string });
          onData(memberData, clubData);
          onReady();
        },
        () => { memberData = null; onData(memberData, clubData); onReady(); }
      );

      clubUnsub.current = onSnapshot(
        doc(db, "clubs", clubId),
        (snap) => {
          clubData = snap.exists()
            ? ({ id: snap.id, ...snap.data() } as Club & { id: string })
            : null;
          onData(memberData, clubData);
          onReady();
        },
        () => { clubData = null; onData(memberData, clubData); onReady(); }
      );
    },
    [cleanup]
  );

  return { subscribe, cleanup };
}

/* ── Provider ── */

export function ClubProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [club, setClub] = useState<(Club & { id: string }) | null>(null);
  const [membership, setMembership] = useState<(Member & { id: string }) | null>(null);
  const [clubIds, setClubIds] = useState<string[]>([]);
  const [ready, setReady] = useState(false);
  const { subscribe, cleanup } = useClubSubscription();

  /*
   * When the user changes:
   *   1. Reset all state, mark not ready
   *   2. Read stored club preference from AsyncStorage
   *   3. Start real-time listener on the user doc → get clubIds
   *   4. Pick the active club (stored preference or first in list)
   *   5. Subscribe to that club's membership + club doc
   *   6. Mark ready once both have fired
   */
  useEffect(() => {
    cleanup();
    setClub(null);
    setMembership(null);
    setClubIds([]);
    setReady(false);

    if (!user) {
      setReady(true);
      return;
    }

    const uid = user.uid;
    let cancelled = false;
    let userUnsub: (() => void) | null = null;

    (async () => {
      const storedClubId = await getItem("activeClubId");
      if (cancelled) return;

      userUnsub = onSnapshot(
        doc(db, "users", uid),
        (snap) => {
          if (cancelled) return;
          const ids: string[] = snap.data()?.clubIds || [];
          setClubIds(ids);

          if (ids.length === 0) {
            cleanup();
            setClub(null);
            setMembership(null);
            setReady(true);
            return;
          }

          const activeId = storedClubId && ids.includes(storedClubId) ? storedClubId : ids[0];

          subscribe(uid, activeId, (m, c) => {
            setMembership(m);
            setClub(c);
          }, () => setReady(true));
        },
        () => { if (!cancelled) setReady(true); }
      );
    })();

    return () => {
      cancelled = true;
      userUnsub?.();
      cleanup();
    };
  }, [user?.uid]);

  /* Switch to a different club (called from UI) */
  const switchClub = useCallback((clubId: string | null) => {
    if (clubId) {
      setItem("activeClubId", clubId);
    } else {
      removeItem("activeClubId");
    }

    if (!clubId || !user) {
      setClub(null);
      setMembership(null);
      return;
    }

    setReady(false);
    subscribe(user.uid, clubId, (m, c) => {
      setMembership(m);
      setClub(c);
    }, () => setReady(true));
  }, [user, subscribe]);

  const loading = authLoading || !ready;

  return (
    <ClubContext.Provider value={{ club, membership, clubIds, loading, switchClub }}>
      {children}
    </ClubContext.Provider>
  );
}

export function useClub() {
  return useContext(ClubContext);
}
